import { useState } from 'react';

/** 자주 쓰는 메일 도메인 — 목록에 없으면 '직접 입력'. */
const EMAIL_DOMAINS = [
  'naver.com',
  'gmail.com',
  'daum.net',
  'hanmail.net',
  'kakao.com',
  'nate.com',
  'outlook.com',
] as const;

const CUSTOM = '__custom__';

const INPUT_CLASS = [
  'block w-full min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-[15px]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
  'dark:border-white/15 dark:bg-dark-card dark:text-neutral-100 dark:placeholder:text-neutral-500',
].join(' ');

interface EmailSplitFieldProps {
  id: string;
  label: string;
  hint?: string;
  /** 있으면 hint 대신 보여준다(FormField 와 같은 규칙). */
  error?: string;
  /** 아이디@도메인 을 합친 값. 어느 한쪽이라도 비면 '' 를 보낸다. */
  onChange: (email: string) => void;
}

/**
 * 이메일 입력 — `아이디` @ `도메인 선택` 으로 나눠 받는다.
 * 목록에 없는 도메인은 '직접 입력' 을 고르면 아래에 입력칸이 열린다.
 * 아이디 칸에 전체 주소를 붙여넣으면 알아서 갈라 넣는다.
 */
export function EmailSplitField({
  id,
  label,
  hint,
  error,
  onChange,
}: EmailSplitFieldProps) {
  const [local, setLocal] = useState('');
  const [choice, setChoice] = useState('');
  const [customDomain, setCustomDomain] = useState('');

  const emit = (l: string, c: string, cd: string) => {
    const domain = c === CUSTOM ? cd.trim() : c;
    onChange(l.trim() && domain ? `${l.trim()}@${domain}` : '');
  };

  const handleLocal = (raw: string) => {
    // 전체 주소 붙여넣기 — `@` 뒤를 도메인 쪽으로 보낸다.
    const at = raw.indexOf('@');
    if (at >= 0) {
      const l = raw.slice(0, at);
      const d = raw
        .slice(at + 1)
        .trim()
        .toLowerCase();
      const known = (EMAIL_DOMAINS as readonly string[]).includes(d);
      const c = d ? (known ? d : CUSTOM) : choice;
      const cd = d && !known ? d : customDomain;
      setLocal(l);
      setChoice(c);
      setCustomDomain(cd);
      emit(l, c, cd);
      return;
    }
    setLocal(raw);
    emit(raw, choice, customDomain);
  };

  const hintId = hint && !error ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = errorId ?? hintId;

  return (
    <div>
      <label
        htmlFor={id}
        className="text-sm text-adobe-muted dark:text-neutral-400"
      >
        {label}
      </label>

      <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5">
        <input
          id={id}
          type="text"
          inputMode="email"
          // 앞부분만 받는 칸이라 브라우저 자동완성(전체 주소·로그인 아이디)이 끼면 오히려 틀린다.
          autoComplete="off"
          placeholder="아이디"
          value={local}
          onChange={(e) => handleLocal(e.target.value)}
          aria-describedby={describedBy}
          aria-invalid={!!error}
          className={INPUT_CLASS}
        />
        <span
          aria-hidden
          className="text-sm font-semibold text-neutral-950/40 dark:text-white/40"
        >
          @
        </span>
        <select
          aria-label="이메일 도메인"
          value={choice}
          onChange={(e) => {
            setChoice(e.target.value);
            emit(local, e.target.value, customDomain);
          }}
          className={`${INPUT_CLASS} ${choice ? '' : 'text-neutral-950/40 dark:text-white/40'}`}
        >
          <option value="" disabled>
            선택
          </option>
          {EMAIL_DOMAINS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          <option value={CUSTOM}>직접 입력</option>
        </select>
      </div>

      {choice === CUSTOM && (
        <input
          type="text"
          inputMode="url"
          aria-label="이메일 도메인 직접 입력"
          placeholder="example.com"
          value={customDomain}
          onChange={(e) => {
            setCustomDomain(e.target.value);
            emit(local, choice, e.target.value);
          }}
          className={`${INPUT_CLASS} mt-1.5`}
        />
      )}

      {hint && !error && (
        <span
          id={hintId}
          className="mt-1 block text-xs text-adobe-muted dark:text-neutral-400"
        >
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} role="alert" className="mt-1 block text-xs text-danger">
          {error}
        </span>
      )}
    </div>
  );
}
